import React, { useState, useEffect } from 'react';
import { ClientLayout } from '../../components/layout/ClientLayout';
import { useCheckPermissions } from '../../middleware/checkPermissions';
import { supabase } from '../../lib/supabase';
import { Loader2, Upload, User, Mail, Phone, MapPin, Globe, Calendar, X } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { useToast } from '../../components/ui/use-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

export const Profile = () => {
  const { user } = useCheckPermissions([]);
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: ''
  });

  // Charger les données de l'utilisateur
  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.id) return;

      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        if (data) {
          setFormData({
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            address: data.address || '',
            city: data.city || '',
            country: data.country || ''
          });
          setAvatarUrl(data.avatar_url);
          setImagePreview(data.avatar_url);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
        toast({
          variant: 'destructive',
          title: 'Erreur',
          description: 'Impossible de charger vos informations.',
        });
      }
    };

    loadUserData();
  }, [user?.id, toast]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    
    if (!file) return;

    // Vérifier la taille du fichier
    if (file.size > MAX_FILE_SIZE) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'La taille maximale du fichier est de 5MB',
      });
      return;
    }

    // Vérifier le type du fichier
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Format accepté : .jpg, .jpeg, .png, .webp',
      });
      return;
    }

    // Créer un aperçu
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload l'image
    handleAvatarUpload(file);
  };

  const handleAvatarUpload = async (file: File) => {
    if (!user?.id) return;

    try {
      setIsLoading(true);

      const fileExt = file.name.split('.').pop();
      const filePath = `avatar_${user.id}.${fileExt}`;

      // Upload l'image
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      // Récupère l'URL publique
      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const publicUrl = data.publicUrl;

      // Met à jour le profil avec la nouvelle URL
      const { error: updateError } = await supabase
        .from('users')
        .update({ 
          avatar_url: publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setAvatarUrl(publicUrl);
      toast({
        title: 'Avatar mis à jour',
        description: 'Votre photo de profil a été mise à jour avec succès.',
      });
    } catch (error) {
      console.error('Erreur lors de l\'upload de l\'avatar:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la mise à jour de l\'avatar.',
      });
      // Restaurer l'ancienne image en cas d'erreur
      setImagePreview(avatarUrl);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!user?.id || !avatarUrl) return;

    try {
      setIsDeleting(true);
      
      // Supprimer l'image de Supabase Storage
      const fileName = avatarUrl.split('/').pop();
      if (fileName) {
        await supabase.storage
          .from('avatars')
          .remove([fileName]);
      }

      // Mettre à jour le profil sans l'URL de l'image
      const { error } = await supabase
        .from('users')
        .update({ 
          avatar_url: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setAvatarUrl(null);
      setImagePreview(null);
      toast({
        title: 'Succès',
        description: "La photo de profil a été supprimée",
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: error.message || "Impossible de supprimer la photo de profil",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setIsLoading(true);

      const { error } = await supabase
        .from('users')
        .update({
          name: formData.name,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          country: formData.country,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;

      toast({
        title: 'Profil mis à jour',
        description: 'Vos informations ont été mises à jour avec succès.',
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      toast({
        variant: 'destructive',
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la mise à jour du profil.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ClientLayout>
      <div className="max-w-4xl mx-auto space-y-8 pt-20">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="absolute -inset-2 bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 rounded-full blur opacity-75"></div>
            <User className="h-8 w-8 text-yellow-400 relative" />
          </div>
          <div>
            <h2 className="text-2xl font-bold">
              <span className="bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 text-transparent bg-clip-text">
                Profil
              </span>
            </h2>
            <p className="text-gray-400 mt-1">
              Gérez vos informations personnelles et préférences
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Section Avatar */}
          <div className="lg:col-span-1">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-lg">
              <div className="space-y-6">
                <div className="relative mx-auto w-32 h-32 group">
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 rounded-full blur opacity-75"></div>
                  <div className="relative rounded-full overflow-hidden w-32 h-32 border-2 border-white/20">
                    <img
                      src={imagePreview || 'https://pgnkuxeckqnzkyfcrwzs.supabase.co/storage/v1/object/public/candidates/logo_66666666-6666-6666-6666-666666666666.jpg'}
                      alt={formData.name}
                      className="w-full h-full object-cover"
                    />
                    {imagePreview && (
                      <button
                        onClick={handleDeleteAvatar}
                        disabled={isDeleting}
                        className="absolute top-0 right-0 p-1 bg-red-500/80 hover:bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        {isDeleting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <X className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-white">{formData.name}</h3>
                  <p className="text-sm text-gray-400">{formData.email}</p>
                </div>
                <div>
                  <input
                    type="file"
                    accept={ACCEPTED_IMAGE_TYPES.join(',')}
                    onChange={handleImageChange}
                    className="hidden"
                    id="avatar-upload"
                    disabled={isLoading}
                  />
                  <label
                    htmlFor="avatar-upload"
                    className="flex items-center justify-center gap-2 w-full px-4 py-2 text-sm font-medium text-white bg-yellow-600/20 hover:bg-yellow-600/30 rounded-lg cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                    Changer l'avatar
                  </label>
                </div>
                {user?.last_login && (
                  <div className="text-center text-sm text-gray-400">
                    <div className="flex items-center justify-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Dernière connexion : {format(new Date(user.last_login), 'PPP', { locale: fr })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section Informations */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-lg space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-400 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Nom complet
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-400 flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email
                  </label>
                  <Input
                    name="email"
                    value={formData.email}
                    disabled
                    className="bg-white/5 border-white/10 text-white opacity-50"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-400 flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    Téléphone
                  </label>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+237 6XX XXX XXX"
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-400 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Adresse
                  </label>
                  <Input
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-400 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Ville
                  </label>
                  <Input
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-400 flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Pays
                  </label>
                  <Input
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  type="submit" 
                  disabled={isLoading}
                  className="bg-yellow-600 hover:bg-yellow-600/90"
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Enregistrer les modifications
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default Profile;
